import { useToast } from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "../../../apiClient";
import { translate } from "../../../i18n";
import { GameDto } from "../../../models/game/dto/game.dto";
import { UpdateGameCourtDto } from "../../../models/game/dto/update-game-court.dto";
import { GAME_QUERY_KEY } from "./useGetGame";

export const useUpdateGameCourt = (gameId: number) => {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();
    const toast = useToast();

    return useMutation({
        mutationKey: ['updateGameCourt', gameId],
        mutationFn: (updateGameCourtDto: UpdateGameCourtDto) => {
            return apiClient.fetchApi<GameDto, UpdateGameCourtDto>({
                method: 'PUT',
                endpoint: `games/${gameId}/update-court`,
                body: updateGameCourtDto
            });
        },
        onSuccess: (updatedGame) => {
            queryClient.setQueryData<GameDto>(GAME_QUERY_KEY(gameId), updatedGame);
            queryClient.invalidateQueries({ queryKey: ['games'] });
            // A court change revokes the old access codes and issues new ones
            // against the new court's door — the displayed PINs are now stale.
            // Narrow key on purpose: invalidating ['game', gameId] would
            // discard the game data just written above.
            queryClient.invalidateQueries({ queryKey: ['game', gameId, 'access-credentials'] });

            toast({
                title: translate('game.court_updated'),
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        },
        onError: (error: any) => {
            toast({
                title: translate('game.error_updating_court'),
                description: error?.message || translate('common.unexpected_error'),
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        },
    });
};
